// biome-ignore-all lint/a11y/useSemanticElements: a virtualised treegrid positions div rows absolutely; table elements cannot
// biome-ignore-all lint/a11y/useFocusableInteractive: rows get their roving tabindex through the spread `common` props
import { CalendarBlank, CaretRight, Check, FlagBanner, Plus } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemCreate, ItemLite } from "@plannello/shared";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AvatarStack } from "@/components/domain/AvatarStack";
import { FieldChip, MoreChip } from "@/components/domain/FieldChip";
import { ItemKey } from "@/components/domain/ItemKey";
import { PriorityIcon } from "@/components/domain/PriorityIcon";
import { ProgressRing } from "@/components/domain/ProgressRing";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { milestoneLabel } from "@/components/planning/milestoneOrder";
import { type ColumnKey, DEFAULT_COLUMNS, type ViewDensity } from "@/components/views/viewState";
import type { Catalog } from "@/data/catalog";
import { isPlaceholder } from "@/data/mutations";
import { TranslatedMark, titleTooltip, useTitles } from "@/data/titles";
import { cx } from "@/lib/cx";
import { fullDateTime, loc, shortAge, shortDate } from "@/lib/format";
import { useCssPx } from "@/lib/useCssPx";
import { useRegisterView } from "./activeView";
import { type ChildStats, itemChips } from "./display";
import type { Group } from "./grouping";
import { SelectionBar } from "./SelectionBar";

const rowId = (r: Row) => (r.type === "group" ? `g:${r.path}` : r.item.id);

type Row =
  | {
      type: "group";
      group: Group;
      /** "status-id" or "status-id/sub-key": the collapse key and the row id. */
      path: string;
      collapsed: boolean;
      /** 1 = group, 2 = sub-group. */
      level: number;
      parentPath?: string;
    }
  | {
      type: "item";
      item: ItemLite;
      group: Group;
      /** Group path the item sits in (Left moves to its header). */
      path: string;
      /** Tree depth under another item of the same group (sub-items). */
      depth: number;
      childCount: number;
      expanded: boolean;
      parentId: string | null;
      baseLevel: number;
    };

/** Row heights per view density; without one the app-wide density (and coarse pointers) apply. */
const DENSITY_PX: Record<ViewDensity, number> = { compact: 32, comfortable: 38, spacious: 46 };

type TreeEntry = { item: ItemLite; depth: number; childCount: number; expanded: boolean };

/**
 * Orders a group's items as a tree: an item whose parent is in the same group follows it,
 * indented. Siblings keep the group's sort order. Collapsed parents hide their descendants.
 */
export function treeOrder(items: readonly ItemLite[], collapsedItems: ReadonlySet<string>): TreeEntry[] {
  const ids = new Set(items.map((i) => i.id));
  const children = new Map<string, ItemLite[]>();
  const roots: ItemLite[] = [];
  for (const i of items) {
    if (i.parentId && i.parentId !== i.id && ids.has(i.parentId)) {
      const list = children.get(i.parentId);
      if (list) list.push(i);
      else children.set(i.parentId, [i]);
    } else roots.push(i);
  }
  const out: TreeEntry[] = [];
  const seen = new Set<string>();
  const walk = (item: ItemLite, depth: number) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    const kids = children.get(item.id) ?? [];
    const expanded = !collapsedItems.has(item.id);
    out.push({ item, depth, childCount: kids.length, expanded });
    if (expanded) for (const k of kids) walk(k, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return out;
}

export interface ItemListProps {
  groups: Group[];
  /** Show group header rows (false for "No grouping"). */
  showHeaders: boolean;
  catalog: Catalog;
  childStats: Map<string, ChildStats>;
  /** Accessible name of the grid, e.g. "Storefront items". */
  label: string;
  peekId?: string | null;
  onOpen: (item: ItemLite) => void;
  onPeek: (item: ItemLite) => void;
  /** Called when keyboard or mouse focus lands on another item row (the peek follows it). */
  onFocusItem?: (item: ItemLite) => void;
  onEscape?: () => void;
  /** Quick-create defaults for the focused group (e.g. its status). */
  createDefaults?: (group: Group | null) => Partial<ItemCreate>;
  onCreateInGroup?: (group: Group) => void;
  /** Collapsed group keys survive remounts per view. */
  viewKey: string;
  /** Sub-items nest under their parent (tree rows) when both are in the same group. */
  tree?: boolean;
  /** Visible properties in display order (default: priority, key, type, status, fields, updated, assignee). */
  columns?: readonly ColumnKey[];
  /** Row density of this view; omitted = the app-wide density. */
  density?: ViewDensity;
  /** Any cached item by id (parent hint for sub-items whose parent is in another group or filtered out). */
  itemById?: (id: string) => ItemLite | undefined;
  className?: string;
}

const collapsedByView = new Map<string, Set<string>>();

/**
 * Dense, virtualised item list (ARIA treegrid: group headers are level-1 rows, sub-group headers
 * level 2, items below them; with `tree`, sub-items nest under their parent one level deeper).
 * One row has tabindex 0 (roving); J/K or arrows move, Enter opens, Space peeks, X selects,
 * Shift+arrows or Shift+click extend the selection. Left collapses a group or a parent item (or
 * moves to the parent row), Right expands it (or moves to its first child).
 * Property keys (S, P, A, L, T, M, 1-4) act on the selection or the focused row through the
 * active-view registry.
 */
export function ItemList({
  groups,
  showHeaders,
  catalog,
  childStats,
  label,
  peekId,
  onOpen,
  onPeek,
  onEscape,
  onFocusItem,
  createDefaults,
  onCreateInGroup,
  viewKey,
  tree = false,
  columns = DEFAULT_COLUMNS,
  density,
  itemById,
  className,
}: ItemListProps) {
  const cssRowH = useCssPx("--row-h", 32);
  // Coarse pointers keep their 48px rows whatever the view asks for.
  const rowH = density ? Math.max(DENSITY_PX[density], cssRowH >= 44 ? cssRowH : 0) : cssRowH;
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => collapsedByView.get(viewKey) ?? new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchorId = useRef<string | null>(null);
  const wantFocus = useRef(false);

  useEffect(() => {
    collapsedByView.set(viewKey, collapsed);
  }, [viewKey, collapsed]);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const pushItems = (g: Group, path: string, baseLevel: number) => {
      const entries: TreeEntry[] = tree
        ? treeOrder(g.items, collapsed)
        : g.items.map((item) => ({ item, depth: 0, childCount: 0, expanded: false }));
      for (const t of entries)
        out.push({
          type: "item",
          item: t.item,
          group: g,
          path,
          depth: t.depth,
          childCount: t.childCount,
          expanded: t.expanded,
          parentId: t.depth > 0 ? t.item.parentId : null,
          baseLevel,
        });
    };
    for (const g of groups) {
      const isCollapsed = showHeaders && collapsed.has(g.key);
      if (showHeaders) out.push({ type: "group", group: g, path: g.key, collapsed: isCollapsed, level: 1 });
      if (isCollapsed) continue;
      if (g.children && g.children.length > 0) {
        for (const sg of g.children) {
          const path = `${g.key}/${sg.key}`;
          const subCollapsed = collapsed.has(path);
          out.push({
            type: "group",
            group: sg,
            path,
            collapsed: subCollapsed,
            level: showHeaders ? 2 : 1,
            parentPath: showHeaders ? g.key : undefined,
          });
          if (!subCollapsed) pushItems(sg, path, showHeaders ? 3 : 2);
        }
      } else pushItems(g, g.key, showHeaders ? 2 : 1);
    }
    return out;
  }, [groups, showHeaders, collapsed, tree]);

  const indexById = useMemo(() => new Map(rows.map((r, i) => [rowId(r), i])), [rows]);
  const itemsById = useMemo(() => {
    const map = new Map<string, ItemLite>();
    const add = (g: Group) => {
      for (const i of g.items) map.set(i.id, i);
      for (const c of g.children ?? []) add(c);
    };
    for (const g of groups) add(g);
    return map;
  }, [groups]);

  // Drop selected ids that left the list (filtered out, deleted).
  useEffect(() => {
    setSelected((s) => {
      const next = new Set([...s].filter((id) => itemsById.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [itemsById]);

  const keyWidth = useMemo(() => {
    let n = 5;
    for (const id of itemsById.values()) n = Math.max(n, id.key.length);
    return n;
  }, [itemsById]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowH,
    overscan: 12,
    // Room below the last row so the floating selection bar never covers it.
    paddingEnd: 72,
    getItemKey: (i) => rowId(rows[i]!),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when density changes the row height
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rowH]);

  // The peek panel follows the focused row.
  const followRef = useRef(onFocusItem);
  followRef.current = onFocusItem;
  useEffect(() => {
    const item = focusedId ? itemsById.get(focusedId) : undefined;
    if (item) followRef.current?.(item);
  }, [focusedId, itemsById]);

  const focusedIndex = focusedId !== null ? (indexById.get(focusedId) ?? -1) : -1;
  const tabIndexRow = focusedIndex >= 0 ? focusedIndex : 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectRange only reads rows and indexById, both listed
  const focusRow = useCallback(
    (index: number, opts: { extend?: boolean } = {}) => {
      const r = rows[Math.max(0, Math.min(rows.length - 1, index))];
      if (!r) return;
      const id = rowId(r);
      wantFocus.current = true;
      setFocusedId(id);
      virtualizer.scrollToIndex(indexById.get(id) ?? 0, { align: "auto" });
      if (opts.extend && r.type === "item") {
        const from = anchorId.current ?? focusedId ?? id;
        selectRange(from, id, true);
      } else if (r.type === "item" && !opts.extend) {
        anchorId.current = id;
      }
    },
    [rows, indexById, virtualizer, focusedId],
  );

  // Move DOM focus after a keyboard move once the row is rendered.
  useLayoutEffect(() => {
    if (!wantFocus.current || !focusedId) return;
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(focusedId)}"]`);
    if (el) {
      wantFocus.current = false;
      el.focus({ preventScroll: true });
    }
  });

  function selectRange(fromId: string, toId: string, additive: boolean) {
    const a = indexById.get(fromId);
    const b = indexById.get(toId);
    if (a === undefined || b === undefined) return;
    const [lo, hi] = a < b ? [a, b] : [b, a];
    setSelected((s) => {
      const next = additive ? new Set(s) : new Set<string>();
      for (let i = lo; i <= hi; i++) {
        const r = rows[i];
        if (r?.type === "item") next.add(r.item.id);
      }
      return next;
    });
  }

  const toggleSelect = useCallback((id: string) => {
    anchorId.current = id;
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const focusedItem = (): ItemLite | null => {
    const r = focusedIndex >= 0 ? rows[focusedIndex] : undefined;
    return r?.type === "item" ? (itemsById.get(r.item.id) ?? null) : null;
  };
  const focusedGroup = (): Group | null => {
    const r = focusedIndex >= 0 ? rows[focusedIndex] : undefined;
    return r ? r.group : null;
  };
  const selectedItems = () => [...selected].map((id) => itemsById.get(id)).filter((i): i is ItemLite => !!i);

  const moveItems = (delta: number) => {
    let i = focusedIndex;
    for (;;) {
      i += delta;
      if (i < 0 || i >= rows.length) {
        if (focusedIndex < 0 && rows.length) i = delta > 0 ? 0 : rows.length - 1;
        else return;
      }
      if (rows[i]?.type === "item") return focusRow(i);
      if (focusedIndex < 0 && i === (delta > 0 ? rows.length - 1 : 0)) return;
    }
  };

  useRegisterView({
    kind: "list",
    root: () => gridRef.current,
    targets: () => {
      const sel = selectedItems();
      if (sel.length) return sel;
      const f = focusedItem();
      return f ? [f] : [];
    },
    anchor: () =>
      (focusedId && gridRef.current?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(focusedId)}"]`)) ||
      gridRef.current,
    move: moveItems,
    open: onOpen,
    peek: onPeek,
    toggleSelect: (i) => toggleSelect(i.id),
    createDefaults: () => createDefaults?.(focusedGroup()) ?? {},
    restoreFocus: () => {
      wantFocus.current = true;
      setFocusedId((id) => id);
      if (focusedId) {
        gridRef.current?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(focusedId)}"]`)?.focus();
      }
    },
  });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && e.target.closest("input, textarea, [contenteditable=true]")) return;
    if (e.metaKey || e.ctrlKey) {
      if (e.key.toLowerCase() === "a") {
        e.preventDefault();
        setSelected(new Set(itemsById.keys()));
      }
      return;
    }
    if (e.altKey) return;
    const r = focusedIndex >= 0 ? rows[focusedIndex] : undefined;
    const page = Math.max(1, Math.floor((scrollRef.current?.clientHeight ?? 400) / rowH) - 1);
    const key = e.key;
    let handled = true;
    if (key === "ArrowDown") focusRow(focusedIndex + 1, { extend: e.shiftKey });
    else if (key === "ArrowUp") focusRow(Math.max(0, focusedIndex - 1), { extend: e.shiftKey });
    else if ((key === "j" || key === "J") && !e.shiftKey) moveItems(1);
    else if ((key === "k" || key === "K") && !e.shiftKey) moveItems(-1);
    else if (key === "Home") focusRow(0);
    else if (key === "End") focusRow(rows.length - 1);
    else if (key === "PageDown") focusRow(focusedIndex + page);
    else if (key === "PageUp") focusRow(Math.max(0, focusedIndex - page));
    else if (key === "Enter" && r) {
      if (r.type === "group") toggleGroup(r.path);
      else onOpen(r.item);
    } else if (key === " " && r) {
      if (r.type === "group") toggleGroup(r.path);
      else onPeek(r.item);
    } else if ((key === "x" || key === "X") && r?.type === "item") toggleSelect(r.item.id);
    else if (key === "ArrowLeft" && r) {
      const parentRow = r.type === "item" && r.parentId ? indexById.get(r.parentId) : undefined;
      const header = indexById.get(`g:${r.type === "group" ? (r.parentPath ?? "") : r.path}`);
      if (r.type === "group" && !r.collapsed) toggleGroup(r.path);
      else if (r.type === "item" && r.childCount > 0 && r.expanded) toggleGroup(r.item.id);
      else if (parentRow !== undefined) focusRow(parentRow);
      else if (header !== undefined) focusRow(header);
      else handled = false;
    } else if (key === "ArrowRight" && r) {
      if (r.type === "group" && r.collapsed) toggleGroup(r.path);
      else if (r.type === "item" && r.childCount > 0 && !r.expanded) toggleGroup(r.item.id);
      else if (r.type === "item" && r.childCount > 0) focusRow(focusedIndex + 1);
      else handled = false;
    } else if (key === "Escape") {
      if (selected.size > 0) setSelected(new Set());
      else if (onEscape) onEscape();
      else handled = false;
    } else handled = false;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const rowFromEvent = (e: { target: EventTarget }) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>("[data-row-id]")?.dataset.rowId;
    const i = id ? indexById.get(id) : undefined;
    return i === undefined ? undefined : rows[i];
  };
  const onGridFocus = (e: FocusEvent<HTMLDivElement>) => {
    const r = rowFromEvent(e);
    if (r && focusedId !== rowId(r)) setFocusedId(rowId(r));
  };
  const onGridClick = (e: MouseEvent<HTMLDivElement>) => {
    const r = rowFromEvent(e);
    if (r) onRowClick(e, r);
  };

  const onRowClick = (e: MouseEvent, r: Row) => {
    if (r.type === "group") {
      setFocusedId(rowId(r));
      toggleGroup(r.path);
      return;
    }
    if ((e.target as HTMLElement).closest("[data-tree-toggle]")) {
      e.preventDefault();
      setFocusedId(r.item.id);
      toggleGroup(r.item.id);
      return;
    }
    e.preventDefault();
    setFocusedId(r.item.id);
    if (e.shiftKey) {
      selectRange(anchorId.current ?? r.item.id, r.item.id, true);
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      toggleSelect(r.item.id);
      return;
    }
    anchorId.current = r.item.id;
    onOpen(r.item);
  };

  const total = itemsById.size;
  const items = virtualizer.getVirtualItems();
  // A string keeps memoised rows from re-rendering when only the array identity changes.
  const columnsKey = columns.join(",");

  return (
    <div className={cx("@container relative flex min-h-0 flex-1 flex-col", className)}>
      <div ref={scrollRef} className="scroll-thin min-h-0 flex-1 overflow-auto">
        <div
          ref={gridRef}
          role="treegrid"
          aria-label={label}
          aria-rowcount={rows.length}
          aria-multiselectable="true"
          tabIndex={-1}
          onKeyDown={onKeyDown}
          onClick={onGridClick}
          onFocus={onGridFocus}
          className="relative w-full outline-none"
          style={{ height: virtualizer.getTotalSize() }}
        >
          <div role="rowgroup" className="absolute inset-x-0 top-0">
            {items.map((v) => {
              const r = rows[v.index]!;
              const id = rowId(r);
              // Handlers live on the grid (event delegation) so memoised rows only re-render
              // when their own data, selection or position changes.
              const common = {
                "data-row-id": id,
                "aria-rowindex": v.index + 1,
                tabIndex: v.index === tabIndexRow ? 0 : -1,
                start: v.start,
                height: rowH,
              };
              return r.type === "group" ? (
                <GroupRow
                  key={v.key}
                  {...common}
                  group={r.group}
                  level={r.level}
                  collapsed={r.collapsed}
                  onCreate={onCreateInGroup ? () => onCreateInGroup(r.group) : undefined}
                />
              ) : (
                <ItemRowView
                  key={v.key}
                  {...common}
                  item={r.item}
                  level={r.baseLevel + r.depth}
                  depth={r.depth}
                  childCount={tree ? r.childCount : 0}
                  expanded={r.expanded}
                  parentHint={tree && r.depth === 0 && r.item.parentId ? (itemById?.(r.item.parentId) ?? null) : null}
                  columns={columnsKey}
                  catalog={catalog}
                  stats={childStats.get(r.item.id)}
                  keyWidth={keyWidth}
                  isSelected={selected.has(r.item.id)}
                  isPeeked={peekId === r.item.id}
                  onToggleSelect={toggleSelect}
                />
              );
            })}
          </div>
        </div>
      </div>
      {selected.size > 0 ? (
        <SelectionBar
          count={selected.size}
          total={total}
          getTargets={selectedItems}
          onClear={() => {
            setSelected(new Set());
            gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
          }}
        />
      ) : null}
    </div>
  );
}

type RowCommon = {
  "data-row-id": string;
  "aria-rowindex": number;
  tabIndex: number;
  start: number;
  height: number;
};

/** Spreads the common row props onto the row element (position via transform). */
const rowAttrs = ({ start, height, ...rest }: RowCommon) => ({
  ...rest,
  style: { transform: `translateY(${start}px)`, height },
});

const rowBase =
  "absolute inset-x-0 top-0 flex items-center outline-hidden select-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-focus";

function GroupRow({
  group,
  level,
  collapsed,
  onCreate,
  ...common
}: RowCommon & { group: Group; level: number; collapsed: boolean; onCreate?: () => void }) {
  return (
    <div
      role="row"
      aria-level={level}
      aria-expanded={!collapsed}
      className={cx(
        rowBase,
        "group/g cursor-default border-b border-line pr-3 hover:bg-hover",
        level > 1 ? "bg-canvas pl-6" : "bg-subtle pl-2",
      )}
      {...rowAttrs(common)}
    >
      <div role="gridcell" className="flex min-w-0 flex-1 items-center gap-2">
        <CaretRight
          size={12}
          weight="bold"
          aria-hidden="true"
          className={cx(
            "shrink-0 text-fg-subtle transition-transform duration-(--dur-fast)",
            !collapsed && "rotate-90",
          )}
        />
        {group.icon ? <span className="flex w-4 shrink-0 justify-center">{group.icon}</span> : null}
        <span className={cx("truncate-title font-medium", level > 1 ? "text-xs text-fg-muted" : "text-sm text-fg")}>
          {group.label}
        </span>
        <span className="text-xs text-fg-subtle tnum">{group.items.length}</span>
      </div>
      {onCreate ? (
        <div role="gridcell" className="flex shrink-0 items-center">
          <button
            type="button"
            tabIndex={-1}
            aria-label={`${m.action_create_item()}: ${group.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onCreate();
            }}
            className="flex size-6 items-center justify-center rounded-control text-fg-subtle opacity-0 transition-ui group-hover/g:opacity-100 hover:bg-active hover:text-fg"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

const ItemRowView = memo(function ItemRowView({
  item,
  level,
  depth,
  childCount,
  expanded,
  parentHint,
  columns,
  catalog,
  stats,
  keyWidth,
  isSelected,
  isPeeked,
  onToggleSelect,
  ...common
}: RowCommon & {
  item: ItemLite;
  level: number;
  depth: number;
  childCount: number;
  expanded: boolean;
  parentHint: ItemLite | null;
  /** Comma-separated ColumnKeys. */
  columns: string;
  catalog: Catalog;
  stats: ChildStats | undefined;
  keyWidth: number;
  isSelected: boolean;
  isPeeked: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const show = new Set(columns.split(","));
  const status = catalog.statusById.get(item.statusId);
  const type = catalog.typeById.get(item.typeId);
  const chips = show.has("fields") ? itemChips(item, catalog) : [];
  const shown = chips.slice(0, 2);
  const people = item.assigneeIds.map((id) => catalog.userById.get(id)).filter((u) => !!u);
  const pending = isPlaceholder(item);
  const milestone = show.has("milestone") && item.milestoneId ? catalog.milestoneById.get(item.milestoneId) : undefined;
  const closed = status?.category === "completed" || status?.category === "canceled";
  const overdue = !!item.dueDate && !closed && item.dueDate < todayIso();
  const isParent = childCount > 0;
  const titleOf = useTitles();
  const shownTitle = titleOf(item);
  const shownParent = parentHint ? titleOf(parentHint) : null;
  return (
    <div
      role="row"
      aria-level={level}
      aria-selected={isSelected}
      aria-expanded={isParent ? expanded : undefined}
      className={cx(
        rowBase,
        "group/row cursor-default gap-2.5 border-b border-line pr-4 pl-2 text-sm",
        isSelected ? "bg-accent-soft" : isPeeked ? "bg-active" : "hover:bg-hover focus-visible:bg-hover",
        pending && "opacity-60",
      )}
      {...rowAttrs(common)}
    >
      <div role="gridcell" className="flex shrink-0 items-center gap-2">
        {/* Selection box: visible on hover or when selected; space is always reserved (no shift). */}
        <span
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
          className={cx(
            "flex size-3.5 shrink-0 items-center justify-center rounded-mark border transition-ui",
            isSelected
              ? "border-accent bg-accent text-accent-fg"
              : "border-line-strong opacity-0 group-hover/row:opacity-100",
          )}
        >
          {isSelected ? <Check size={10} weight="bold" /> : null}
        </span>
        {show.has("priority") ? (
          <span data-picker-anchor="priority" className="flex">
            <PriorityIcon value={item.priority} />
          </span>
        ) : null}
        {show.has("key") ? <ItemKey value={item.key} width={keyWidth} /> : null}
      </div>
      <div role="gridcell" className="flex min-w-0 flex-1 items-center gap-2">
        {depth > 0 ? <span aria-hidden="true" className="shrink-0" style={{ width: depth * 20 }} /> : null}
        {isParent ? (
          <span
            data-tree-toggle=""
            aria-hidden="true"
            title={expanded ? m.tree_collapse() : m.tree_expand()}
            className="-mx-1 flex size-5 shrink-0 items-center justify-center rounded-mark text-fg-subtle hover:bg-active hover:text-fg"
          >
            <CaretRight
              size={11}
              weight="bold"
              className={cx("transition-transform duration-(--dur-fast)", expanded && "rotate-90")}
            />
          </span>
        ) : depth > 0 ? (
          <span aria-hidden="true" className="flex w-3 shrink-0 justify-center">
            <span className="h-px w-2 bg-line-strong opacity-60" />
          </span>
        ) : null}
        {show.has("type") ? (
          <span data-picker-anchor="type" className="flex @max-md:hidden">
            {type ? (
              <TypeIcon name={type.icon} color={type.color} label={loc(type.name)} className="text-fg-subtle" />
            ) : null}
          </span>
        ) : null}
        {show.has("status") ? (
          <span data-picker-anchor="status" className="flex">
            {status ? <StatusIcon category={status.category} label={loc(status.name)} /> : null}
          </span>
        ) : null}
        <a
          href={`/${item.key}`}
          tabIndex={-1}
          onClick={(e) => e.preventDefault()}
          className={cx("truncate-title", isParent ? "font-medium text-fg" : "text-fg")}
          title={titleTooltip(shownTitle, item.title)}
        >
          {shownTitle.text}
          <TranslatedMark d={shownTitle} />
        </a>
        {stats ? (
          <span className="flex shrink-0 items-center gap-1 text-xs text-fg-subtle tnum">
            <ProgressRing
              done={stats.done}
              total={stats.total}
              label={m.item_children_progress({ done: stats.done, total: stats.total })}
            />
            <span aria-hidden="true">
              {stats.done}/{stats.total}
            </span>
          </span>
        ) : null}
        {parentHint ? (
          <span
            className="max-w-56 min-w-0 shrink truncate-title text-xs text-fg-subtle @max-3xl:hidden"
            title={`${parentHint.key} ${titleTooltip(shownParent!, parentHint.title)}`}
          >
            <span className="sr-only">{m.tree_parent()}: </span>
            <span aria-hidden="true">{parentHint.key} </span>
            {shownParent!.text}
          </span>
        ) : null}
      </div>
      {show.has("fields") ? (
        <div role="gridcell" data-picker-anchor="labels" className="flex shrink-0 items-center gap-1.5 @max-3xl:hidden">
          {shown.map((c) => (
            <FieldChip key={c.id} label={c.label} color={c.color} field={c.field} />
          ))}
          {chips.length > shown.length ? (
            <MoreChip
              count={chips.length - shown.length}
              label={chips
                .slice(shown.length)
                .map((c) => (c.field ? `${c.field}: ${c.label}` : c.label))
                .join(", ")}
            />
          ) : null}
        </div>
      ) : null}
      <div role="gridcell" className="flex shrink-0 items-center justify-end gap-2.5">
        {show.has("milestone") ? (
          <span
            data-picker-anchor="milestone"
            className="flex w-24 min-w-0 items-center gap-1 text-xs text-fg-muted @max-3xl:hidden"
            title={milestone ? milestoneLabel(milestone) : undefined}
          >
            {milestone ? (
              <>
                <FlagBanner size={12} aria-hidden="true" className="shrink-0 text-fg-subtle" />
                <span className="sr-only">{m.prop_milestone()}: </span>
                <span className="truncate-title">{milestone.version ?? milestone.name}</span>
              </>
            ) : null}
          </span>
        ) : null}
        {show.has("start") ? (
          <DateCell value={item.startDate} label={m.prop_start()} className="@max-3xl:hidden" />
        ) : null}
        {show.has("due") ? (
          <DateCell value={item.dueDate} label={m.prop_due()} overdue={overdue} className="@max-xl:hidden" />
        ) : null}
        {show.has("estimate") ? (
          <span className="w-8 text-right text-xs text-fg-subtle tnum @max-xl:hidden">
            {item.estimate !== null ? (
              <>
                <span className="sr-only">{m.prop_estimate()}: </span>
                {item.estimate}
              </>
            ) : null}
          </span>
        ) : null}
        {show.has("created") ? (
          <span
            className="w-14 text-right text-xs whitespace-nowrap text-fg-subtle tnum @max-3xl:hidden"
            title={fullDateTime(item.createdAt)}
          >
            {item.createdAt ? shortAge(item.createdAt) : null}
          </span>
        ) : null}
        {show.has("updated") ? (
          <span
            className="w-14 text-right text-xs whitespace-nowrap text-fg-subtle tnum @max-md:hidden"
            title={fullDateTime(item.updatedAt)}
          >
            {shortAge(item.updatedAt)}
          </span>
        ) : null}
        {show.has("assignee") ? (
          <span data-picker-anchor="assignee" className="flex w-8 justify-end">
            <AvatarStack people={people} size="sm" />
          </span>
        ) : null}
      </div>
    </div>
  );
});

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Start or due date in a row: short date, "overdue" carried by text for screen readers and an icon tint. */
function DateCell({
  value,
  label,
  overdue,
  className,
}: {
  value: string | null;
  label: string;
  overdue?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "flex w-16 items-center justify-end gap-1 text-xs tnum",
        overdue ? "text-danger-text" : "text-fg-subtle",
        className,
      )}
      title={value ? `${label}: ${shortDate(value)}${overdue ? `, ${m.date_overdue()}` : ""}` : undefined}
    >
      {value ? (
        <>
          <CalendarBlank size={12} weight={overdue ? "fill" : "regular"} aria-hidden="true" className="shrink-0" />
          <span className="sr-only">{label}: </span>
          {shortDate(value)}
          {overdue ? <span className="sr-only">, {m.date_overdue()}</span> : null}
        </>
      ) : null}
    </span>
  );
}
